import React from "react";
import PropTypes from "prop-types";

import { createClassNameString, insuranceCompanyFilterOptions } from "../utils";

import { Card } from "../Card";

import { EmptyProfileInsurance } from "./EmptyProfileInsurance";
import { EditProfileInsurance } from "./EditProfileInsurance";
import { CompletedProfileInsurance } from "./CompletedProfileInsurance";

import "./styles.scss";

export const MemberProfileInsurance = ({
  className,
  isEmpty,
  isEditing,
  onEditButtonClick,
  onAddButtonClick,

  // -- Empty Props
  emptyTitle,
  emptyDetails,

  // -- Edit Props
  editTitle,

  // Formik props
  values,
  errors,
  touched,
  handleChange,
  handleFocus,
  handleBlur,

  insuranceCompaniesOptions,
  stateOfInsurancePlanOptions,
  policyHolderOptions,

  files,
  onBrowseButtonClicked,
  onFileDelete,

  // -- Completed Props
  completedTitle,

  insuranceCompany,
  policyHolder,
  memberId,
  groupId,
  stateOfInsurancePlan,
  policyHolderName,
  policyHolderDOB,

  insuranceCardImgFrontSrc,
  insuranceCardImgFrontStatus,
  insuranceCardImgBackSrc,
  insuranceCardImgBackStatus,
  onInsuranceCardImgFrontClick,
  onInsuranceCardImgBackClick,

  onSubmitButtonClick,
  onCancelButtonClick,

  // Carrier Specific
  memberIdPlaceholder,
  memberIdTip,
  memberIdTooltipContents,
  groupIdTooltipContents,

  noInsurance,

  isAso,
  ...props
}) => {
  return (
    <Card className={createClassNameString(["apti-MemberProfileInsurance", className])} {...props}>
      {isEmpty && !isEditing && (
        <EmptyProfileInsurance
          details={emptyDetails}
          onAddButtonClick={onAddButtonClick}
          title={emptyTitle}
        />
      )}

      {isEditing && (
        <EditProfileInsurance
          errors={errors}
          files={files}
          groupIdTooltipContents={groupIdTooltipContents}
          handleBlur={handleBlur}
          handleChange={handleChange}
          handleFocus={handleFocus}
          insuranceCompaniesOptions={insuranceCompaniesOptions}
          insuranceCompanyFilterOptions={insuranceCompanyFilterOptions}
          memberIdPlaceholder={memberIdPlaceholder}
          memberIdTip={memberIdTip}
          memberIdTooltipContents={memberIdTooltipContents}
          noInsurance={noInsurance}
          onBrowseButtonClicked={onBrowseButtonClicked}
          onCancelButtonClick={onCancelButtonClick}
          onFileDelete={onFileDelete}
          onSubmitButtonClick={onSubmitButtonClick}
          policyHolderOptions={policyHolderOptions}
          stateOfInsurancePlanOptions={stateOfInsurancePlanOptions}
          title={editTitle}
          touched={touched}
          values={values}
        />
      )}
      {!isEmpty && !isEditing && (
        <CompletedProfileInsurance
          groupId={groupId}
          insuranceCardImgBackSrc={insuranceCardImgBackSrc}
          insuranceCardImgBackStatus={insuranceCardImgBackStatus}
          insuranceCardImgFrontSrc={insuranceCardImgFrontSrc}
          insuranceCardImgFrontStatus={insuranceCardImgFrontStatus}
          insuranceCompany={insuranceCompany}
          memberId={memberId}
          onEditButtonClick={onEditButtonClick}
          onInsuranceCardImgBackClick={onInsuranceCardImgBackClick}
          onInsuranceCardImgFrontClick={onInsuranceCardImgFrontClick}
          policyHolder={policyHolder}
          policyHolderDOB={policyHolderDOB}
          policyHolderName={policyHolderName}
          stateOfInsurancePlan={stateOfInsurancePlan}
          isAso={isAso}
          title={completedTitle}
        />
      )}
    </Card>
  );
};

MemberProfileInsurance.propTypes = {
  className: PropTypes.string,
  isEmpty: PropTypes.bool,
  isEditing: PropTypes.bool,
  onEditButtonClick: PropTypes.func,
  onAddButtonClick: PropTypes.func,

  // -- Empty Props
  emptyTitle: PropTypes.string,
  emptyDetails: PropTypes.string,

  // -- Edit Props
  editTitle: PropTypes.string,

  // Formik props
  values: PropTypes.object,
  errors: PropTypes.object,
  touched: PropTypes.object,
  handleChange: PropTypes.func,
  handleFocus: PropTypes.func,
  handleBlur: PropTypes.func,

  insuranceCompaniesOptions: PropTypes.arrayOf(PropTypes.object),
  stateOfInsurancePlanOptions: PropTypes.arrayOf(PropTypes.object),
  policyHolderOptions: PropTypes.arrayOf(PropTypes.object),

  files: PropTypes.arrayOf(PropTypes.string),
  onBrowseButtonClicked: PropTypes.func,
  onFileDelete: PropTypes.func,

  // -- Completed Props
  completedTitle: PropTypes.string,

  insuranceCompany: PropTypes.string,
  policyHolder: PropTypes.string,
  memberId: PropTypes.string,
  groupId: PropTypes.string,
  stateOfInsurancePlan: PropTypes.string,
  policyHolderName: PropTypes.string,
  policyHolderDOB: PropTypes.string,

  insuranceCardImgFrontSrc: PropTypes.string,
  insuranceCardImgFrontStatus: PropTypes.string,

  insuranceCardImgBackSrc: PropTypes.string,
  insuranceCardImgBackStatus: PropTypes.string,

  onInsuranceCardImgFrontClick: PropTypes.func,
  onInsuranceCardImgBackClick: PropTypes.func,

  onSubmitButtonClick: PropTypes.func,
  onCancelButtonClick: PropTypes.func,

  noInsurance: PropTypes.bool,
};

MemberProfileInsurance.defaultProps = {
  className: undefined,
  isEmpty: false,
  isEditing: false,
  onEditButtonClick: () => {},
  onAddButtonClick: () => {},

  // -- Empty Props
  emptyTitle: "",
  emptyDetails: "",

  // -- Edit Props
  editTitle: "",

  // Formik props
  values: {},
  errors: {},
  touched: {},
  handleChange: () => {},
  handleFocus: () => {},
  handleBlur: () => {},

  insuranceCompaniesOptions: [],
  stateOfInsurancePlanOptions: [],
  policyHolderOptions: [],

  files: [],
  onBrowseButtonClicked: () => {},
  onFileDelete: () => {},

  // -- Completed Props
  completedTitle: "",

  insuranceCompany: "",
  policyHolder: "",
  memberId: "",
  groupId: "",
  stateOfInsurancePlan: "",
  policyHolderName: "",
  policyHolderDOB: "",

  insuranceCardImgFrontSrc: "",
  insuranceCardImgFrontStatus: "",

  insuranceCardImgBackSrc: "",
  insuranceCardImgBackStatus: "",

  onInsuranceCardImgFrontClick: () => {},
  onInsuranceCardImgBackClick: () => {},

  onSubmitButtonClick: () => {},
  onCancelButtonClick: () => {},

  noInsurance: false,
};
